/**
 * Worker pool coordinator.
 *
 * Splits a run into chunks, distributes them, and reduces the returned
 * accumulators in chunk order.
 *
 * The chunk count is a power of two derived from the block plan — NOT from the
 * core count. That is the whole trick: a power-of-two count of equal, aligned
 * chunks means each chunk is a complete subtree of the canonical reduction
 * tree, so combining chunk results in order reproduces the single-threaded
 * reduction bit for bit. Hardware then decides only *who* runs each chunk and
 * in what order they finish, never how the arithmetic associates.
 */

import type { Project } from '../engine/model.ts';
import { buildModel } from '../engine/model.ts';
import {
  blockPlan,
  pilotRanges,
  assembleResult,
  PairwiseReducer,
  type MonteCarloResult,
} from '../engine/montecarlo.ts';
import { RunAccumulator } from '../engine/accumulator.ts';
import type { WorkerRequest, WorkerResponse, ChunkDone } from './protocol.ts';

export interface RunProgress {
  completed: number;
  total: number;
  fraction: number;
}

export interface PoolRunOptions {
  iterations: number;
  seed?: number;
  bins?: number;
  subsampleLimit?: number;
  onProgress?: (p: RunProgress) => void;
  signal?: AbortSignal;
}

/** How many workers to start. One core is left for the UI thread. */
export function defaultWorkerCount(): number {
  const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 2 : 2;
  return Math.max(1, Math.min(16, cores - 1));
}

export class McPool {
  private workers: Worker[] = [];
  private readonly size: number;

  constructor(size = defaultWorkerCount()) {
    this.size = size;
  }

  private ensureStarted(): void {
    if (this.workers.length > 0) return;
    for (let i = 0; i < this.size; i++) {
      this.workers.push(
        new Worker(new URL('./mc.worker.ts', import.meta.url), { type: 'module' }),
      );
    }
  }

  terminate(): void {
    for (const w of this.workers) w.terminate();
    this.workers = [];
  }

  async run(project: Project, options: PoolRunOptions): Promise<MonteCarloResult> {
    const iterations = Math.max(1, Math.floor(options.iterations));
    const seed = options.seed ?? project.seed;
    const bins = options.bins ?? 4096;
    const subsampleLimit = options.subsampleLimit ?? 65536;

    // Built on the coordinator too: it validates the project before any worker
    // is handed it, and the pilot pass and result assembly both need it.
    const model = buildModel(project);
    const started = Date.now();
    const plan = blockPlan(iterations);
    const ranges = pilotRanges(model, seed, iterations, bins);

    // Chunk count: a power of two, at least the worker count so every worker
    // has something to do, never more than the number of blocks.
    let chunks = 1;
    while (chunks < this.size && chunks < plan.count) chunks <<= 1;
    const blocksPerChunk = plan.count / chunks;

    this.ensureStarted();

    const results = new Array<ChunkDone['accumulator']>(chunks).fill(null);
    const blocksDone = new Array<number>(chunks).fill(0);
    let nextChunk = 0;
    let settled = 0;
    let failure: string | null = null;
    let cancelled = false;

    const totalBlocks = plan.count;
    const reportProgress = () => {
      if (!options.onProgress) return;
      const done = blocksDone.reduce((a, b) => a + b, 0);
      const fraction = Math.min(1, done / totalBlocks);
      options.onProgress({
        completed: Math.round(fraction * iterations),
        total: iterations,
        fraction,
      });
    };

    await new Promise<void>((resolve) => {
      const dispatch = (worker: Worker) => {
        if (nextChunk >= chunks || cancelled) return;
        const chunkIndex = nextChunk++;
        const req: WorkerRequest = {
          type: 'run-chunk',
          chunkIndex,
          project,
          seed,
          firstBlock: chunkIndex * blocksPerChunk,
          blockCount: blocksPerChunk,
          plan,
          iterations,
          ranges,
          subsampleLimit,
        };
        worker.postMessage(req);
      };

      const onAbort = () => {
        cancelled = true;
        for (const w of this.workers) w.postMessage({ type: 'cancel' } as WorkerRequest);
      };
      options.signal?.addEventListener('abort', onAbort, { once: true });

      for (const worker of this.workers) {
        worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
          const msg = event.data;

          if (msg.type === 'progress') {
            blocksDone[msg.chunkIndex] = msg.blocksDone;
            reportProgress();
            return;
          }

          if (msg.type === 'error') {
            failure ??= msg.message;
            settled++;
          } else {
            results[msg.chunkIndex] = msg.accumulator;
            blocksDone[msg.chunkIndex] = blocksPerChunk;
            settled++;
            reportProgress();
          }

          if (settled >= chunks || (cancelled && nextChunk >= chunks)) {
            options.signal?.removeEventListener('abort', onAbort);
            resolve();
          } else {
            dispatch(worker);
          }
        };

        worker.onerror = (e) => {
          failure ??= e.message || 'Worker crashed';
          settled++;
          if (settled >= chunks) resolve();
        };
      }

      // Prime every worker. Chunks are claimed in ascending order, but the
      // order they COMPLETE in is irrelevant — reduction happens by index.
      for (const worker of this.workers) dispatch(worker);
      if (chunks === 0) resolve();
    });

    if (failure) throw new Error(`Monte Carlo failed: ${failure}`);

    // Reduce strictly by chunk index, never by arrival order.
    const reducer = new PairwiseReducer();
    for (let i = 0; i < chunks; i++) {
      const t = results[i];
      if (!t) continue;
      reducer.push(RunAccumulator.fromTransfer(t));
    }

    const run = reducer.finish();
    if (run === null) throw new Error('Run produced no results');

    return assembleResult(model, run, iterations, seed, started, cancelled);
  }
}
